import { Address } from '../components/AddressLookup';
import {
  COLLECTION_FEE,
  COLLECTION_SURCHARGE,
  SHIPPING_FEE,
} from '../constants';
import { BoxData, Discount } from '../models/APIModels';
import { isOutOfArea } from './Postcode';

const deliveryFeeString = process.env.REACT_APP_DELIVERY_FEE;
if (!deliveryFeeString) {
  throw new Error('No delivery fee set');
}
const deliveryFee = parseInt(deliveryFeeString);

export function toMoney(num: number): string {
  return `£${(num / 100).toFixed(2)}`;
}

export function boxToMoney(box: BoxData, discount?: Discount) {
  const discountMultiplier =
    discount?.recurring === false ? 1 - discount.discountPercentage / 100 : 1;
  return toMoney(box.count * box.purchaseCost * discountMultiplier);
}

export function monthlyCostToMoney(boxes: BoxData[], discount?: Discount) {
  const discountMultiplier = discount?.recurring
    ? 1 - discount.discountPercentage / 100
    : 1;

  const cost = boxes
    ? boxes.reduce((acc, box) => acc + box.count * box.storageCost, 0)
    : 0;
  return toMoney(cost * discountMultiplier);
}

export function purchaseCostToMoney(
  boxes: BoxData[],
  discount?: Discount,
  includeEmptyBoxesRate: boolean = false,
): string {
  const discountMultiplier = discount?.upFront
    ? 1 - discount.discountPercentage / 100
    : 1;

  let cost = boxes
    ? boxes.reduce((acc, box) => acc + box.count * box.purchaseCost, 0)
    : 0;

  if (includeEmptyBoxesRate) cost += deliveryFee;
  return toMoney(cost * discountMultiplier);
}

export function payNow(
  boxes: BoxData[],
  discount?: Discount,
  includeEmptyBoxesRate: boolean = false,
  address?: Address,
): string {
  const discountMultiplier = discount?.upFront
    ? 1 - discount.discountPercentage / 100
    : 1;

  let cost = boxes
    ? boxes.reduce((acc, box) => acc + box.count * box.purchaseCost, 0)
    : 0;

  if (includeEmptyBoxesRate) cost += deliveryFee;

  cost += COLLECTION_FEE;

  if (address && address.postCode && isOutOfArea(address.postCode)) {
    cost += COLLECTION_SURCHARGE;
  }

  return toMoney(cost * discountMultiplier);
}

export function orderCostToMoney(
  boxes: BoxData[],
  includeEmptyBoxesRate: boolean,
  discount?: Discount,
): string {
  const discountMultiplier =
    discount?.recurring === false ? 1 - discount.discountPercentage / 100 : 1;

  const itemsWithShippingCosts = boxes.filter((it) => it.chargeShipping);
  const itemsWithoutShippingCosts = boxes.filter((it) => !it.chargeShipping);

  const purchaseCostToMoney = itemsWithShippingCosts.reduce(
    (purchaseCostToMoney, box) => {
      return purchaseCostToMoney + box.count * box.purchaseCost;
    },
    0,
  );

  const totalBoxes = boxes
    .filter((it) => it.chargeShipping)
    .map((it) => it.count);
  const totalBoxesCount = totalBoxes?.reduce((a, b) => a + b, 0);

  const ownBoxesCount = boxes
    .filter((it) => it.chargeShipping && it.name === 'Own box')
    .map((it) => it.count)
    .reduce((a, b) => a + b, 0);
  const otherBoxesCount = totalBoxesCount - ownBoxesCount;

  const shippingRate = SHIPPING_FEE;
  const deliveryCost =
    shippingRate * otherBoxesCount + (shippingRate + 500) * ownBoxesCount;

  const orderCostWithShipping = purchaseCostToMoney + deliveryCost;

  let orderCostingWithoutShipping = 0;
  if (itemsWithoutShippingCosts.length) {
    for (let i = 0; i < itemsWithoutShippingCosts.length; i += 1) {
      const { purchaseCost, count } = itemsWithoutShippingCosts[i];
      orderCostingWithoutShipping += purchaseCost * count;
    }
  }

  return toMoney(
    boxes
      ? includeEmptyBoxesRate
        ? (orderCostingWithoutShipping + orderCostWithShipping + deliveryFee) *
          discountMultiplier
        : (orderCostingWithoutShipping + orderCostWithShipping) *
          discountMultiplier
      : 0,
  );
}

export function suitcaseOrderToMoney(
  suitcaseQuantity: number,
  suitcaseShippingCost?: number,
  discount?: Discount,
): string {
  const discountMultiplier = discount
    ? 1 - discount.discountPercentage / 100
    : 1;

  return suitcaseShippingCost
    ? toMoney(suitcaseQuantity * suitcaseShippingCost * discountMultiplier)
    : '£0.00';
}
